import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import { toast } from "react-hot-toast";
import CameraCapture from "../../../components/shared/CameraCapture";
import moment from "moment";
import { useLanguage } from "../../../context/LanguageContext";
import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Clock,
  Droplets,
  Filter,
  TestTube,
  Calendar,
  FileText,
  Info,
  Send,
  Camera,
} from "lucide-react";

const SectionCWaterSupply = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  // Helper function to convert YES/NO/NA to numbers
  const yesNoNaToNumber = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    if (value === "NA") return 3;
    return null;
  };

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("survey_section_c");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setForm((prev) => ({
          ...prev,
          c1: parsedData.c1 || "",
          c2: parsedData.c2 || "",
          c3: parsedData.c3 || "",
          c3Date: parsedData.c3Date || "",
          c3Remarks: parsedData.c3Remarks || "",
          c4: parsedData.c4 || "",
          c4Date: parsedData.c4Date || "",
          c4Remarks: parsedData.c4Remarks || "",
        }));
        // Handle file objects - we only store file names for display
        if (parsedData.c3PhotoName) {
          setForm((prev) => ({
            ...prev,
            c3Photo: { name: parsedData.c3PhotoName },
          }));
        }
        if (parsedData.c4ReportName) {
          setForm((prev) => ({
            ...prev,
            c4Report: { name: parsedData.c4ReportName },
          }));
        }
        if (parsedData.isPartial === true) {
          toast(t.global.alert_saved_locally, { icon: "📝" });
        }
      } catch (error) {
        console.error("Error loading saved data:", error);
      }
    }
  }, [t]);

  const [form, setForm] = useState({
    c1: "",
    c2: "",
    c3: "",
    c3Date: "",
    c3Photo: null,
    c3Remarks: "",
    c4: "",
    c4Date: "",
    c4Report: null,
    c4Remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({});

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      let allowedTypes;
      if (name === "c3Photo") {
        allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (file && !allowedTypes.includes(file.type)) {
          setErrors((prev) => ({
            ...prev,
            [name]: t.global.err_file_type_long,
          }));
          return;
        }
      } else if (name === "c4Report") {
        allowedTypes = [
          "application/pdf",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ];
        if (file && !allowedTypes.includes(file.type)) {
          setErrors((prev) => ({
            ...prev,
            [name]: t.global.err_file_type_long,
          }));
          return;
        }
      }

      setForm((prev) => ({ ...prev, [name]: file }));
      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);

        setPreviewImages((prev) => ({
          ...prev,
          [name]: previewUrl,
        }));
      } else {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: null,
        }));
      }

      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations - only main questions are required
    if (!form.c1) newErrors.c1 = t.global.err_a1;
    if (!form.c2) newErrors.c2 = t.global.err_a1;
    if (!form.c3) newErrors.c3 = t.global.err_a1;
    if (!form.c4) newErrors.c4 = t.global.err_a1;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(t.global.alert_fix_errors);
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();

      // LOCATION IDS
      fd.append("district_id", state.district_id);
      fd.append("municipality_id", state.municipality_id);
      fd.append("ward_id", state.ward_id);
      fd.append("area_id", state.area_id);
      fd.append("canteen_type_id", state.canteen_type_id);

      if (state?.parent_kitchen?.id) {
        fd.append("parent_kitchen_id", state.parent_kitchen.id);
      }

      // WATER SUPPLY DATA
      fd.append("water_supply_C1", yesNoNaToNumber(form.c1));
      fd.append("water_supply_C2", yesNoNaToNumber(form.c2));
      fd.append("water_supply_C3", yesNoNaToNumber(form.c3));
      fd.append("water_supply_C4", yesNoNaToNumber(form.c4));

      // CONDITIONAL FIELDS - these are optional
      if (form.c3 === t.global.yes && form.c3Date) {
        fd.append("water_supply_C3_date", form.c3Date);
      }
      if (form.c3 === t.global.yes && form.c3Photo) {
        fd.append("water_supply_C3_upload", form.c3Photo);
      }
      if (form.c3Remarks) {
        fd.append("water_supply_C3_remarks", form.c3Remarks);
      }

      if (form.c4 === t.global.yes && form.c4Date) {
        fd.append("water_supply_C4_date", form.c4Date);
      }
      if (form.c4 === t.global.yes && form.c4Report) {
        fd.append("water_supply_C4_upload", form.c4Report);
      }
      if (form.c4Remarks) {
        fd.append("water_supply_C4_remarks", form.c4Remarks);
      }

      // META
      fd.append("current_section", "water");
      fd.append("reporting_date", moment().format("YYYY-MM-DD"));

      // Check if section is fully complete (all optional fields filled)
      const isFullyComplete =
        (form.c3 !== t.global.yes ||
          (form.c3Date && form.c3Photo && form.c3Remarks)) &&
        (form.c4 !== t.global.yes || (form.c4Date && form.c4Report && form.c4Remarks));

      if (!isFullyComplete) {
        fd.append("is_partial", "1");
      }

      // API CALL
      await dispatch(createKitchen(fd));

      // Show success message
      setShowSuccess(true);

      // Save to localStorage with partial flag
      const sectionData = {
        ...form,
        c3PhotoName: form.c3Photo?.name || "",
        c4ReportName: form.c4Report?.name || "",
        isPartial: !isFullyComplete,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_c", JSON.stringify(sectionData));

      // Mark Section C as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.c = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error(error);
      toast.error(t.global.alert_server_error);

      // Save locally for offline anyway
      const isFullyComplete =
        (form.c3 !== t.global.yes ||
          (form.c3Date && form.c3Photo && form.c3Remarks)) &&
        (form.c4 !== t.global.yes || (form.c4Date && form.c4Report && form.c4Remarks));

      const sectionData = {
        ...form,
        c3PhotoName: form.c3Photo?.name || "",
        c4ReportName: form.c4Report?.name || "",
        isPartial: !isFullyComplete,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_c", JSON.stringify(sectionData));

      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.c = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.success(t.global.alert_saved_locally);
      setIsSubmitting(false);
    }
  };

  const optionsWithNA = [t.global.yes, t.global.no, "NA"];
  const optionsWithoutNA = [t.global.yes, t.global.no];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section C submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchen.sections.C.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchen.sections.C.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center">
                <Droplets size={14} className="mr-1" />
                Water Supply Section
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* C1 - Water Storage Capacity */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Droplets size={18} className="mr-2" />
                C1. {t.modules.kitchen.sections.C.questions.storage}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {optionsWithNA.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.c1 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      } ${errors.c1 ? "border-red-300" : ""}`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="c1"
                            value={option}
                            checked={form.c1 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.c1 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.c1 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>

                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {errors.c1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c1}
                </p>
              )}

              <p className="text-sm text-blue-600 mt-2 flex items-center">
                <Info size={14} className="mr-1" />
                {t.modules.kitchen.sections.C.hints.storage}
              </p>
            </div>

            {/* C2 - Water Filter Installed */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Filter size={18} className="mr-2" />
                C2. {t.modules.kitchen.sections.C.questions.filter}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {optionsWithoutNA.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.c2 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      } ${errors.c2 ? "border-red-300" : ""}`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="c2"
                            value={option}
                            checked={form.c2 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.c2 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.c2 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>

                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {errors.c2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c2}
                </p>
              )}
            </div>

            {/* C3 - Tank Cleaning */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2">
                C3. {t.modules.kitchen.sections.C.questions.tankCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <p className="text-sm text-gray-600 mb-4 flex items-center">
                <Info size={14} className="mr-1" />
                *Please mention the last date of tank cleaning and upload the picture of the tank cleaning with date printed (Optional)
              </p>

              {/* RADIO CARDS */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                {optionsWithNA.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.c3 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      } ${errors.c3 ? "border-red-300" : ""}`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="c3"
                            value={option}
                            checked={form.c3 === option}
                            onChange={handleChange}
                            className="sr-only"
                            required
                            disabled={isSubmitting}
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.c3 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.c3 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>

                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {errors.c3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c3}
                </p>
              )}

              {/* YES SECTION */}
              {form.c3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  {/* DATE */}
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center text-gray-700">
                      <Calendar size={16} className="mr-2" />
                      Last date of tank cleaning (Optional):
                    </label>

                    <input
                      type="date"
                      name="c3Date"
                      value={form.c3Date}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full border border-gray-300 p-3 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  {/* PHOTO SECTION */}
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Upload tank cleaning photo (Optional):
                    </label>

                    {/* CAMERA + UPLOAD BUTTONS */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("c3Photo");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="c3Photo"
                        className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                          isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    <input
                      type="file"
                      name="c3Photo"
                      id="c3Photo"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />

                    {/* PREVIEW BOX */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                        form.c3Photo
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.c3Photo ? (
                        <div className="space-y-3">
                          {/* IMAGE PREVIEW */}
                          {previewImages.c3Photo && (
                            <img
                              src={previewImages.c3Photo}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded-lg shadow"
                            />
                          )}

                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <p className="text-gray-700 font-medium text-sm break-all">
                                {form.c3Photo.name}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                setForm((prev) => ({ ...prev, c3Photo: null }));
                                setPreviewImages((prev) => ({ ...prev, c3Photo: null }));
                              }}
                              className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          Capture or upload tank cleaning photo
                        </p>
                      )}
                    </div>

                    {errors.c3Photo && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.c3Photo}
                      </p>
                    )}
                  </div>

                  {/* REMARKS */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Remarks (Optional):
                    </label>

                    <textarea
                      name="c3Remarks"
                      value={form.c3Remarks}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      rows="2"
                      placeholder="Add any additional remarks..."
                      className="w-full border border-gray-300 p-3 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* C4 - Water Quality Testing */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2">
                C4. {t.modules.kitchen.sections.C.questions.waterTesting}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <p className="text-sm text-gray-600 mb-4 flex items-center">
                <Info size={14} className="mr-1" />
                *Please mention the last date of the water quality testing and upload the test report with date printed (Optional)
              </p>

              {/* RADIO CARDS (Same as C3) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                {optionsWithNA.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.c4 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      } ${errors.c4 ? "border-red-300" : ""}`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="c4"
                            value={option}
                            checked={form.c4 === option}
                            onChange={handleChange}
                            className="sr-only"
                            required
                            disabled={isSubmitting}
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.c4 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.c4 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>

                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {errors.c4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c4}
                </p>
              )}

              {/* YES SECTION - EXACT SAME STYLE AS C3 */}
              {form.c4 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  {/* DATE */}
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center text-gray-700">
                      <Calendar size={16} className="mr-2" />
                      Last date of water quality testing (Optional):
                    </label>

                    <input
                      type="date"
                      name="c4Date"
                      value={form.c4Date}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full border border-gray-300 p-3 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  {/* REPORT SECTION */}
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Upload water testing report (Optional):
                    </label>

                    {/* CAMERA + UPLOAD BUTTONS */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("c4Report");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="c4Report"
                        className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                          isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    <input
                      type="file"
                      name="c4Report"
                      id="c4Report"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />

                    {/* PREVIEW BOX */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                        form.c4Report
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.c4Report ? (
                        <div className="space-y-3">
                          {/* IMAGE PREVIEW (only if image, not PDF) */}
                          {previewImages.c4Report && (
                            <img
                              src={previewImages.c4Report}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded-lg shadow"
                            />
                          )}

                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <p className="text-gray-700 font-medium text-sm break-all">
                                {form.c4Report.name}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                setForm((prev) => ({ ...prev, c4Report: null }));
                                setPreviewImages((prev) => ({ ...prev, c4Report: null }));
                              }}
                              className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          Capture or upload water testing report
                        </p>
                      )}
                    </div>

                    {errors.c4Report && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.c4Report}
                      </p>
                    )}
                  </div>

                  {/* REMARKS */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Remarks (Optional):
                    </label>

                    <textarea
                      name="c4Remarks"
                      value={form.c4Remarks}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      rows="2"
                      placeholder="Add any additional remarks..."
                      className="w-full border border-gray-300 p-3 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  isSubmitting || !form.c1 || !form.c2 || !form.c3 || !form.c4
                }
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting || !form.c1 || !form.c2 || !form.c3 || !form.c4
                    ? "bg-blue-400 cursor-not-allowed text-white"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    {t.global.submit_section_c}
                  </>
                )}
              </button>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-600 font-medium">
                 {t.global.mandatoryFields}
                </span>
                <p className="text-sm text-gray-600 mt-1 flex items-center justify-center">
                  <Info size={14} className="mr-1" />
                  {t.global.optionalNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            if (file.type.startsWith("image/")) {
              const previewUrl = URL.createObjectURL(file);
              setPreviewImages((prev) => ({
                ...prev,
                [cameraField]: previewUrl,
              }));
            }

            setShowCamera(false);
            setCameraField(null);
          }}
          onClose={() => {
            setShowCamera(false);
            setCameraField(null);
          }}
        />
      )}
    </div>
  );
};

export default SectionCWaterSupply;